import type { UseInfiniteQueryResult } from '@tanstack/react-query'
import { useState } from 'react'
import {
  type UsersAutocompleteResponse,
  useUsersAutocomplete
} from '~/api/queries/user'
import { Dropdown } from '~/components/Form'
import type {
  BaseDropdownProps,
  DropdownOption,
  MultiDropdownProps,
  SingleDropdownProps
} from '~/components/Form/Input/Dropdown'
import {
  optionItemElementLabel,
  optionItemElementLabelMain,
  optionItemElementSublabel
} from '~/components/Form/Input/Dropdown.css'
import { formatName } from '~/utils/names'

const USER_OPTION_ESTIMATE_SIZE = 52

const userElement = (user: UsersAutocompleteResponse) => {
  const agenciesLabel = user.agencies
    .map((agency) => agency.name)
    .slice(0, 2)
    .join(' · ')
  const sublabelParts = [user.function, agenciesLabel].filter(Boolean)
  return (
    <div className={optionItemElementLabel}>
      <div className={optionItemElementLabelMain}>{`${formatName(user)}`}</div>
      {(user.function || user.agencies.length > 0) && (
        <div className={optionItemElementSublabel}>
          {sublabelParts.join(' · ')}
          {user.agencies.length > 2 && <span>...</span>}
        </div>
      )}
    </div>
  )
}

type UsersAutocompleteProps = {
  ids?: string[]
  listingContext?: string
} & BaseDropdownProps &
  (
    | {
        multiple?: false
        onChange?: (value: string) => void
      }
    | {
        multiple: true
        onChange?: (value: string[]) => void
      }
  )

export const UsersAutocomplete = ({
  ids,
  listingContext,
  multiple = false,
  onChange,
  name,
  watch,
  ...props
}: UsersAutocompleteProps) => {
  const [search, setSearch] = useState<string>()

  let currentValues = ids
  if (watch && name) {
    currentValues = watch(name) ?? ids
  }

  const { data, ...rest } = useUsersAutocomplete({
    ids: search !== undefined ? undefined : currentValues,
    search: search,
    listingContext
  })
  const options = data?.pages
    .flatMap((page) => page['hydra:member'])
    .map((user) => ({
      label: formatName(user),
      value: String(user.id),
      element: userElement(user),
      estimateSize: USER_OPTION_ESTIMATE_SIZE
    }))

  const queryProps = {
    optionsInfiniteQuery: {
      ...rest,
      data: options
    } as unknown as UseInfiniteQueryResult<DropdownOption[] | undefined>,
    onSearchChange: setSearch
  }

  if (multiple) {
    return (
      <Dropdown
        {...props}
        {...queryProps}
        multiple={true}
        defaultValues={ids}
        onChange={onChange as MultiDropdownProps['onChange']}
        name={name}
        watch={watch}
      />
    )
  }

  return (
    <Dropdown
      {...props}
      {...queryProps}
      multiple={false}
      defaultValue={ids?.[0]}
      onChange={onChange as SingleDropdownProps['onChange']}
    />
  )
}
